import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Option } from '@interfaces/common/option';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  LOGIN_NOTICE_STORAGE_KEY,
  readImpersonationToken,
  takeImpersonationEnterToken,
  takeImpersonationRestore,
} from '@services/auth/impersonation-token.util';
import { fromUnixTime } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { AuthenticationService } from '@services/http/authentication.service';
import { VaultBaseComponent } from '@shared/components/base/vault-base.component';
import { LoginResponse } from '@src/app/core/models/auth/login-response';
import { setLanguage } from '@state/auth/auth.actions';
import { selectAuthState } from '@state/auth/auth.selectors';
import { AuthState } from '@state/auth/auth.state';
import { AppState } from '@state/index';
import { Subject, takeUntil } from 'rxjs';
import { LoginBackgroundSettings } from './components/login-background-designer/login-background-designer.component';

@Component({
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent extends VaultBaseComponent implements OnInit, OnDestroy {
  private readonly minBackgroundRotationDelayMs = 3000;
  private readonly maxBackgroundRotationDelayMs = 5000;
  private readonly backgroundTransitionMs = 2200;
  private readonly backgroundPresets: (Pick<LoginBackgroundSettings, 'image' | 'positionX'> &
    Partial<Pick<LoginBackgroundSettings, 'blur'>>)[] = [
    { image: 'assets/mcp-guide/hero-background.png', positionX: 50 },
    { image: 'assets/mcp-guide/hero-background.png', positionX: 0 },
    { image: 'assets/mcp-guide/hero-background.png', positionX: 100 },
    { image: 'assets/mcp-guide/hero-background2.png', positionX: 69, blur: 8 },
    { image: 'assets/mcp-guide/hero-background3.png', positionX: 0 },
    { image: 'assets/mcp-guide/hero-background4.png', positionX: 0 },
    { image: 'assets/mcp-guide/hero-background4.png', positionX: 100 },
    { image: 'assets/mcp-guide/hero-background4.png', positionX: 30 },
    { image: 'assets/mcp-guide/hero-background5.png', positionX: 27 },
    { image: 'assets/mcp-guide/hero-background5.png', positionX: 60 },
    { image: 'assets/mcp-guide/hero-background5.png', positionX: 100 },
    { image: 'assets/mcp-guide/hero-background6.png', positionX: 61 },
    { image: 'assets/mcp-guide/hero-background6.png', positionX: 5 },
    { image: 'assets/mcp-guide/hero-background6.png', positionX: 100 },
    { image: 'assets/mcp-guide/hero-background7.png', positionX: 0 },
    { image: 'assets/mcp-guide/hero-background7.png', positionX: 60 },
    { image: 'assets/mcp-guide/hero-background7.png', positionX: 100 },
    { image: 'assets/mcp-guide/hero-background8.png', positionX: 0 },
    { image: 'assets/mcp-guide/hero-background8.png', positionX: 35 },
    { image: 'assets/mcp-guide/hero-background8.png', positionX: 100 },
    { image: 'assets/mcp-guide/footer-background.png', positionX: 0 },
    { image: 'assets/mcp-guide/footer-background.png', positionX: 55 },
    { image: 'assets/mcp-guide/footer-background.png', positionX: 100 },
  ];
  private destroy$ = new Subject<void>();
  private backgroundShortcutStep: 'idle' | 'b' = 'idle';
  private backgroundShortcutTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private backgroundRotationTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private backgroundTransitionTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private backgroundTransitionFrameId: number | null = null;
  private backgroundPresetIndex = 0;
  readonly showBackgroundDesigner = signal(false);
  readonly backgroundSettings = signal<LoginBackgroundSettings>({
    image: 'assets/mcp-guide/hero-background.png',
    size: 'cover',
    positionX: 50,
    positionY: 44,
    overlay: 14,
    blur: 0,
    brightness: 100,
    contrast: 100,
    effect: 'none',
  });
  readonly nextBackgroundSettings = signal<LoginBackgroundSettings | null>(null);
  readonly isBackgroundTransitioning = signal(false);
  readonly activeBackgroundLayer = signal<'a' | 'b'>('a');
  readonly backgroundLayerASettings = signal<LoginBackgroundSettings>(this.backgroundSettings());
  readonly backgroundLayerBSettings = signal<LoginBackgroundSettings>(this.backgroundSettings());
  readonly backgroundPointer = signal({
    x: 50,
    y: 50,
    offsetX: 0,
    offsetY: 0,
    tiltX: 0,
    tiltY: 0,
  });
  readonly loginCoverStyle = computed<Record<string, string>>(() => {
    const settings = this.backgroundSettings();
    const pointer = this.backgroundPointer();
    const effect = settings.effect;
    const hasParallax = effect === 'parallax' || effect === 'parallax-spotlight';
    const hasSpotlight = effect === 'spotlight' || effect === 'parallax-spotlight';
    const hasTilt = effect === 'tilt';

    return {
      '--login-bg-image': `url('${settings.image}')`,
      '--login-bg-size': settings.size,
      '--login-bg-position': `${settings.positionX}% ${settings.positionY}%`,
      '--login-bg-overlay': String(settings.overlay / 100),
      '--login-bg-blur': `${settings.blur}px`,
      '--login-bg-brightness': `${settings.brightness}%`,
      '--login-bg-contrast': `${settings.contrast}%`,
      '--login-bg-offset-x': hasParallax ? `${pointer.offsetX}px` : '0px',
      '--login-bg-offset-y': hasParallax ? `${pointer.offsetY}px` : '0px',
      '--login-spotlight-x': `${pointer.x}%`,
      '--login-spotlight-y': `${pointer.y}%`,
      '--login-spotlight-opacity': hasSpotlight ? '0.21' : '0',
      '--login-tilt-x': hasTilt ? `${pointer.tiltX}deg` : '0deg',
      '--login-tilt-y': hasTilt ? `${pointer.tiltY}deg` : '0deg',
    };
  });
  readonly backgroundLayerAStyle = computed<Record<string, string>>(() =>
    this.getBackgroundLayerStyle(this.backgroundLayerASettings())
  );
  readonly backgroundLayerBStyle = computed<Record<string, string>>(() =>
    this.getBackgroundLayerStyle(this.backgroundLayerBSettings())
  );
  public currentAuthState: AuthState = {} as AuthState;
  _login: string = 'login';
  _passwordRecovery: string = 'passwordRecovery';
  _firstAccess: string = 'firstAccess';
  formMode: 'login' | 'passwordRecovery' | 'firstAccess' = 'login';
  defaultLanguage: string = 'pt-BR';
  languageOptions: Option[] = [
    { label: 'Português-BR', value: 'pt-BR' },
    { label: 'Espanhol-ES', value: 'es-ES' },
    { label: 'Inglês-US', value: 'en-US' },
  ];
  companyId: string = '';
  tenantId: string = '';
  /** JWT recebido em `?token=` (login direto por token, usado em testes autenticados). */
  loginToken: string = '';
  skipCompanyScopeSelection: boolean = false;
  logoUrl: string = '';
  private readonly toast = inject(ToastHelperService);
  /** `?restore=1`: volta do "Entrar como" para a conta do ADMIN (card 3804). */
  restoreRequested = false;
  /**
   * Troca de sessão em andamento (`?token=` ou `?restore=1`): no lugar do formulário a tela mostra o carregando com
   * este título e texto. `null` mostra o formulário.
   */
  readonly switchingSession = signal<{ title: string; message: string } | null>(null);

  constructor(
    store: Store<AppState>,
    translateService: TranslateService,
    private readonly activatedRoute: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private utilsService: UtilsService,
    private route: ActivatedRoute
  ) {
    super(translateService, store);
    this.defaultLanguage = translateService.currentLang;
    if (this.store) {
      this.store
        .select(selectAuthState)
        .pipe(takeUntil(this.destroy$))
        .subscribe((authState: AuthState) => {
          this.currentAuthState = authState;
        });
    }
  }

  ngOnInit(): void {
    // Adiciona timestamp para forçar reload da animação do SVG
    this.logoUrl = `assets/images/logo-cognito-animated.svg?v=${Date.now()}`;
    this.getPathParams();
    this.startBackgroundRotation();
    this.showPendingLoginNotice();
  }

  /**
   * Mostra o aviso deixado por um logout forçado (ex.: "Sair" do "Entrar como este usuário" quando a sessão do
   * administrador já venceu, card 3804) e apaga o aviso para não repetir no próximo F5.
   */
  private showPendingLoginNotice(): void {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.sessionStorage?.getItem(LOGIN_NOTICE_STORAGE_KEY);
      if (!raw) return;
      window.sessionStorage.removeItem(LOGIN_NOTICE_STORAGE_KEY);
      const notice = JSON.parse(raw) as { title?: string; message?: string };
      if (notice?.message) {
        this.toast.showToastWarn(notice.title ?? '', notice.message);
      }
    } catch {
      // aviso ilegível: segue sem ele
    }
  }

  override ngOnDestroy(): void {
    this.utilsService.setIsLoading(false);
    this.resetBackgroundShortcut();
    this.stopBackgroundRotation();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getPathParams() {
    this.route.queryParams.subscribe(params => {
      this.companyId = params['companyId'] || '';
      this.tenantId = params['tenantId'] || '';
      this.loginToken = params['token'] || '';
      this.skipCompanyScopeSelection = params['skipCompanyScopeSelection'] === 'true';
      this.restoreRequested = params['restore'] === '1';
      // "Entrar como" (card 3804): o token do usuário vem do sessionStorage desta aba, não da URL.
      if (params['enter'] === '1') {
        this.loginToken =
          takeImpersonationEnterToken(
            typeof window !== 'undefined' ? window.sessionStorage : undefined
          ) ?? '';
      }
    });

    if (this.restoreRequested) {
      void this.restoreImpersonationOrigin();
      return;
    }
    void this.loginWithUrlToken();
  }

  async loginWithUrlToken() {
    if (this.loginToken) {
      // No lugar do formulário, um estado próprio: "Entrar como" (card 3804) diz em nome de quem; token comum, "Entrando…".
      const impersonation = readImpersonationToken(this.loginToken);
      this.switchingSession.set(
        impersonation
          ? {
              title: `Entrando na conta de ${impersonation.targetName}`,
              message:
                'Estamos registrando este acesso no histórico e carregando as permissões. As outras abas deste navegador foram desconectadas.',
            }
          : { title: 'Entrando…', message: '' }
      );
      const pathToRedirect = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
      const payload: LoginResponse = {
        authenticated: true,
        expiresAt: new Date(),
        refreshToken: '',
        bearerToken: this.loginToken,
      };
      const opened = await this.authenticationService.handleLogin(payload, pathToRedirect, {
        skipCompanyScopeSelection: this.skipCompanyScopeSelection,
      });
      // Falhou: o formulário volta, com o toast de erro que o handleLogin já mostrou.
      if (!opened) this.switchingSession.set(null);
    }
  }

  /**
   * Volta do "Entrar como" (card 3804, `?restore=1`): lê e APAGA a sessão do ADMIN guardada nesta aba e entra pelo
   * mesmo caminho do login (permissões, escopo de empresa e renovação), preservando o refresh token dele. Sem sessão
   * válida, mostra o formulário com o aviso de sessão expirada.
   */
  private async restoreImpersonationOrigin(): Promise<void> {
    const restore = takeImpersonationRestore(
      typeof window !== 'undefined' ? window.sessionStorage : undefined
    );
    if (!restore) {
      this.toast.showToastWarn(
        'Sua sessão de administrador expirou',
        'Sua sessão de administrador expirou; entre de novo.'
      );
      return;
    }

    this.switchingSession.set({
      title: 'Voltando para a sua conta',
      message: restore.targetName
        ? `Encerrando o acesso como ${restore.targetName} e carregando as suas permissões.`
        : 'Encerrando o acesso e carregando as suas permissões.',
    });
    const pathToRedirect = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
    const payload: LoginResponse = {
      authenticated: true,
      expiresAt: this.tokenExpiration(restore.bearerToken),
      refreshToken: restore.refreshToken,
      bearerToken: restore.bearerToken,
    };
    const opened = await this.authenticationService.handleLogin(payload, pathToRedirect, {
      skipCompanyScopeSelection: true,
    });
    if (!opened) this.switchingSession.set(null);
  }

  /**
   * Vencimento do token (o `handleLogin` recalcula pelo próprio token; aqui é só para preencher o contrato).
   * @param token JWT.
   */
  private tokenExpiration(token: string): Date {
    try {
      return fromUnixTime(jwtDecode<{ exp?: number }>(token).exp ?? 0);
    } catch {
      return fromUnixTime(0);
    }
  }

  handleFormMode(formMode: string = '') {
    const modes = ['login', 'passwordRecovery', 'firstAccess'];
    const hasMode = modes.includes(formMode);
    if (hasMode) {
      this.switchFormMode(formMode as 'login' | 'passwordRecovery' | 'firstAccess');
    }
  }

  switchFormMode(mode: 'login' | 'passwordRecovery' | 'firstAccess') {
    this.formMode = mode;
  }

  get selectedLanguage(): Option | null {
    return this.languageOptions.find(option => option.value === this.defaultLanguage) || null;
  }

  handleLanguageChange(language: Option | null) {
    if (!language || !this.store) return;
    this.defaultLanguage = language.value;
    this.store.dispatch(setLanguage({ language: this.defaultLanguage }));
  }

  handleBackgroundSettingsChange(settings: LoginBackgroundSettings): void {
    this.clearBackgroundTransition();
    this.backgroundSettings.set(settings);
    this.backgroundLayerASettings.set(settings);
    this.backgroundLayerBSettings.set(settings);
    this.activeBackgroundLayer.set('a');
    this.nextBackgroundSettings.set(null);
    this.syncBackgroundPresetIndex(settings);
  }

  handleBackgroundPointerMove(event: MouseEvent): void {
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;

    const bounds = target.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;
    const intensity = 0.55;

    this.backgroundPointer.set({
      x,
      y,
      offsetX: (50 - x) * 0.42 * intensity,
      offsetY: (50 - y) * 0.42 * intensity,
      tiltX: (50 - y) * 0.08 * intensity,
      tiltY: (x - 50) * 0.08 * intensity,
    });
  }

  resetBackgroundPointer(): void {
    this.backgroundPointer.set({
      x: 50,
      y: 50,
      offsetX: 0,
      offsetY: 0,
      tiltX: 0,
      tiltY: 0,
    });
  }

  @HostListener('window:keydown', ['$event'])
  handleBackgroundDesignerShortcut(event: KeyboardEvent): void {
    if (!event.altKey) {
      this.resetBackgroundShortcut();
      return;
    }

    const key = event.key.toLowerCase();

    if (key === 'b') {
      event.preventDefault();
      this.backgroundShortcutStep = 'b';
      this.scheduleBackgroundShortcutReset();
      return;
    }

    if (key === 'g' && this.backgroundShortcutStep === 'b') {
      event.preventDefault();
      this.showBackgroundDesigner.set(true);
      this.resetBackgroundShortcut();
    }
  }

  private scheduleBackgroundShortcutReset(): void {
    if (this.backgroundShortcutTimeoutId) {
      clearTimeout(this.backgroundShortcutTimeoutId);
    }

    this.backgroundShortcutTimeoutId = setTimeout(() => this.resetBackgroundShortcut(), 1200);
  }

  private resetBackgroundShortcut(): void {
    this.backgroundShortcutStep = 'idle';

    if (this.backgroundShortcutTimeoutId) {
      clearTimeout(this.backgroundShortcutTimeoutId);
      this.backgroundShortcutTimeoutId = null;
    }
  }

  private startBackgroundRotation(): void {
    this.stopBackgroundRotation();
    this.scheduleNextBackgroundRotation();
  }

  private stopBackgroundRotation(): void {
    if (this.backgroundRotationTimeoutId) {
      clearTimeout(this.backgroundRotationTimeoutId);
      this.backgroundRotationTimeoutId = null;
    }

    this.clearBackgroundTransition();
  }

  private scheduleNextBackgroundRotation(): void {
    const delayMs = this.getNextBackgroundRotationDelay();
    this.backgroundRotationTimeoutId = setTimeout(() => {
      this.backgroundRotationTimeoutId = null;
      this.rotateBackground();
    }, delayMs);
  }

  private rotateBackground(): void {
    if (this.isBackgroundTransitioning()) {
      this.scheduleNextBackgroundRotation();
      return;
    }

    const preset = this.getNextBackgroundPreset();
    const nextSettings: LoginBackgroundSettings = {
      ...this.backgroundSettings(),
      image: preset.image,
      positionX: preset.positionX,
      blur: preset.blur ?? 0,
    };

    const inactiveLayer = this.activeBackgroundLayer() === 'a' ? 'b' : 'a';
    this.setBackgroundLayerSettings(inactiveLayer, nextSettings);
    this.nextBackgroundSettings.set(nextSettings);
    this.backgroundTransitionFrameId = requestAnimationFrame(() => {
      this.activeBackgroundLayer.set(inactiveLayer);
      this.isBackgroundTransitioning.set(true);
      this.backgroundTransitionFrameId = null;
    });
    this.backgroundTransitionTimeoutId = setTimeout(() => {
      this.backgroundSettings.set(nextSettings);
      this.nextBackgroundSettings.set(null);
      this.isBackgroundTransitioning.set(false);
      this.backgroundTransitionTimeoutId = null;
      this.scheduleNextBackgroundRotation();
    }, this.backgroundTransitionMs);
  }

  private getNextBackgroundRotationDelay(): number {
    const rangeMs = this.maxBackgroundRotationDelayMs - this.minBackgroundRotationDelayMs;
    return this.minBackgroundRotationDelayMs + Math.round(Math.random() * rangeMs);
  }

  private clearBackgroundTransition(): void {
    if (this.backgroundTransitionFrameId) {
      cancelAnimationFrame(this.backgroundTransitionFrameId);
      this.backgroundTransitionFrameId = null;
    }

    if (this.backgroundTransitionTimeoutId) {
      clearTimeout(this.backgroundTransitionTimeoutId);
      this.backgroundTransitionTimeoutId = null;
    }

    this.isBackgroundTransitioning.set(false);
  }

  private setBackgroundLayerSettings(layer: 'a' | 'b', settings: LoginBackgroundSettings): void {
    if (layer === 'a') {
      this.backgroundLayerASettings.set(settings);
      return;
    }

    this.backgroundLayerBSettings.set(settings);
  }

  private syncBackgroundPresetIndex(settings: LoginBackgroundSettings): void {
    const presetIndex = this.backgroundPresets.findIndex(
      preset =>
        preset.image === settings.image &&
        preset.positionX === settings.positionX &&
        (preset.blur ?? 0) === settings.blur
    );

    if (presetIndex >= 0) {
      this.backgroundPresetIndex = presetIndex;
    }
  }

  private getNextBackgroundPreset(): Pick<LoginBackgroundSettings, 'image' | 'positionX'> &
    Partial<Pick<LoginBackgroundSettings, 'blur'>> {
    const currentSettings = this.backgroundSettings();
    let nextIndex = this.backgroundPresetIndex;

    for (let attempts = 0; attempts < this.backgroundPresets.length; attempts++) {
      nextIndex = (nextIndex + 1) % this.backgroundPresets.length;
      const preset = this.backgroundPresets[nextIndex];

      if (!this.isSameBackgroundPreset(preset, currentSettings)) {
        this.backgroundPresetIndex = nextIndex;
        return preset;
      }
    }

    return this.backgroundPresets[this.backgroundPresetIndex];
  }

  private isSameBackgroundPreset(
    preset: Pick<LoginBackgroundSettings, 'image' | 'positionX'> &
      Partial<Pick<LoginBackgroundSettings, 'blur'>>,
    settings: LoginBackgroundSettings
  ): boolean {
    return (
      preset.image === settings.image &&
      preset.positionX === settings.positionX &&
      (preset.blur ?? 0) === settings.blur
    );
  }

  private getBackgroundLayerStyle(settings: LoginBackgroundSettings): Record<string, string> {
    return {
      '--login-bg-image': `url('${settings.image}')`,
      '--login-bg-size': settings.size,
      '--login-bg-position': `${settings.positionX}% ${settings.positionY}%`,
      '--login-bg-blur': `${settings.blur}px`,
      '--login-bg-brightness': `${settings.brightness}%`,
      '--login-bg-contrast': `${settings.contrast}%`,
    };
  }
}
